/**
 * Utility functions for QR Code generation (PromptPay)
 * Uses promptpay-qr library for accurate QR Code generation
 */
import generatePayload from 'promptpay-qr';

/**
 * Calculate CRC16 checksum for EMV QR Code (CRC-16/CCITT-FALSE)
 * This is the standard CRC16 used in EMV QR Code specification
 * @param {string} data - QR Code data string (including '6304' for CRC calculation)
 * @returns {string} - 4-digit CRC16 hex string (uppercase)
 */
export const calculateCRC16 = (data) => {
  const polynomial = 0x1021; // CRC-16-CCITT polynomial (x^16 + x^12 + x^5 + 1)
  let crc = 0xFFFF; // Initial value (all bits set to 1)
  
  // Process each byte in the data string
  for (let i = 0; i < data.length; i++) {
    const byte = data.charCodeAt(i);
    crc ^= (byte << 8); // XOR with high byte
    
    // Process 8 bits
    for (let j = 0; j < 8; j++) {
      if (crc & 0x8000) {
        // If MSB is 1, shift and XOR with polynomial
        crc = ((crc << 1) ^ polynomial) & 0xFFFF;
      } else {
        // If MSB is 0, just shift
        crc = (crc << 1) & 0xFFFF;
      }
    }
  }
  
  // Return as 4-digit uppercase hex string
  return crc.toString(16).toUpperCase().padStart(4, '0');
};

/**
 * Validate PromptPay number
 * @param {string} promptPayNumber - PromptPay number to validate
 * @returns {object} - { isValid: boolean, type: 'phone'|'id'|null, error: string }
 */
export const validatePromptPayNumber = (promptPayNumber) => {
  if (!promptPayNumber) {
    return {
      isValid: false,
      type: null,
      error: 'กรุณากรอก PromptPay Number'
    };
  }

  // ลบตัวอักษรที่ไม่ใช่ตัวเลข
  const cleaned = promptPayNumber.replace(/\D/g, '');
  
  if (cleaned.length === 0) {
    return {
      isValid: false,
      type: null,
      error: 'PromptPay Number ต้องเป็นตัวเลขเท่านั้น'
    };
  }

  // ตรวจสอบว่าเป็นเบอร์โทรศัพท์ (10 หลัก)
  if (cleaned.length === 10) {
    // ตรวจสอบว่าเริ่มต้นด้วย 0 หรือ 6, 8, 9
    if (!/^[0689]/.test(cleaned)) {
      return {
        isValid: false,
        type: 'phone',
        error: 'เบอร์โทรศัพท์ต้องเริ่มต้นด้วย 0, 6, 8, หรือ 9'
      };
    }
    
    return {
      isValid: true,
      type: 'phone',
      error: null
    };
  }

  // ตรวจสอบว่าเป็นเลขบัตรประชาชน (13 หลัก)
  if (cleaned.length === 13) {
    // ตรวจสอบ checksum ของเลขบัตรประชาชนไทย
    if (!validateThaiIDChecksum(cleaned)) {
      return {
        isValid: false,
        type: 'id',
        error: 'เลขบัตรประชาชนไม่ถูกต้อง (checksum ไม่ผ่าน)'
      };
    }
    
    return {
      isValid: true,
      type: 'id',
      error: null
    };
  }

  return {
    isValid: false,
    type: null,
    error: 'PromptPay Number ต้องเป็นเบอร์โทรศัพท์ (10 หลัก) หรือเลขบัตรประชาชน (13 หลัก)'
  };
};

/**
 * Validate Thai ID card checksum
 * @param {string} idNumber - 13-digit Thai ID number
 * @returns {boolean} - true if valid
 */
const validateThaiIDChecksum = (idNumber) => {
  if (idNumber.length !== 13) return false;
  
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(idNumber[i]) * (13 - i);
  }
  
  const checkDigit = (11 - (sum % 11)) % 10;
  return checkDigit === parseInt(idNumber[12]);
};

/**
 * Format TLV (Tag-Length-Value) for EMV QR Code
 * @param {string} tag - Tag (2 digits)
 * @param {string} value - Value
 * @returns {string} - Formatted TLV string
 */
export const formatTLV = (tag, value) => {
  const valueStr = String(value);
  const length = String(valueStr.length).padStart(2, '0');
  return tag + length + valueStr;
};

/**
 * Generate PromptPay QR Code data string
 * Uses promptpay-qr library for accurate QR Code generation
 * Compatible with SCB, BAY, and all Thai banks
 * @param {string} promptPayNumber - PromptPay number (10 or 13 digits)
 * @param {number} amount - Transaction amount (optional)
 * @returns {string} - QR Code data string with CRC
 */
export const generatePromptPayQRData = (promptPayNumber, amount = 0) => {
  // Validate input
  const validation = validatePromptPayNumber(promptPayNumber);
  if (!validation.isValid) {
    throw new Error(validation.error);
  }

  // Clean PromptPay number
  const cleaned = promptPayNumber.replace(/\D/g, '');
  
  // Use promptpay-qr library to generate correct QR Code payload
  // This library handles all the complex EMV QR Code formatting correctly
  // Function signature: generatePayload(target, options)
  // options: { amount: number } (optional)
  try {
    const options = amount > 0 ? { amount: amount } : {};
    const qrData = generatePayload(cleaned, options);
    
    // Log for debugging
    console.log('QR Code Data (using promptpay-qr):', qrData);
    console.log('PromptPay Number:', cleaned);
    console.log('Amount:', amount);
    
    return qrData;
  } catch (error) {
    console.error('Error generating QR Code with promptpay-qr:', error);
    throw new Error('เกิดข้อผิดพลาดในการสร้าง QR Code: ' + error.message);
  }
};

